import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  Users, 
  DollarSign, 
  Shield, 
  Gift,
  MoreVertical,
  Ban,
  CheckCircle,
  Package,
  TrendingUp,
  Search,
  Plus,
  Coins
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [realmAnalytics, setRealmAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState("");
  
  // Pricing Dialog
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingData, setPricingData] = useState({
    free_price: 0,
    starter_price: 9.99,
    premium_price: 19.99,
    pro_price: 29.99,
    platinum_price: 39.99,
  });
  const [savingPricing, setSavingPricing] = useState(false);
  const [tokenPackPrices, setTokenPackPrices] = useState({
    pack_50: 4.99,
    pack_150: 9.99,
    pack_500: 24.99,
  });

  // Offer Dialog
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerData, setOfferData] = useState({
    target_type: "all",
    target_email: "",
    offer_type: "free_months",
    value: 1,
    description: ""
  });
  const [savingOffer, setSavingOffer] = useState(false);

  // Send Tokens (quick action) Dialog
  const [sendTokensOpen, setSendTokensOpen] = useState(false);
  const [sendTokensUser, setSendTokensUser] = useState(null);
  const [sendTokensAmount, setSendTokensAmount] = useState(50);
  const [sendTokensReason, setSendTokensReason] = useState("");
  const [sendingTokens, setSendingTokens] = useState(false);

  useEffect(() => {
    fetchData();
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      const plans = response.data.plans || [];
      const priceMap = {};
      plans.forEach(plan => {
        priceMap[plan.name.toLowerCase()] = plan.price;
      });
      setPricingData({
        free_price: priceMap.free || 0,
        starter_price: priceMap.starter || 9.99,
        premium_price: priceMap.premium || 19.99,
        pro_price: priceMap.pro || 29.99,
        platinum_price: priceMap.platinum || 39.99,
      });
      const packsRes = await axios.get(`${API}/tokens/packs`);
      const packMap = {};
      (packsRes.data.packs || []).forEach((p) => { packMap[p.id] = p.price; });
      setTokenPackPrices((prev) => ({ ...prev, ...packMap }));
    } catch (error) {
      console.error("Failed to fetch pricing:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, offersRes, realtimeRes, realmRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/offers`),
        axios.get(`${API}/admin/realtime-stats`),
        axios.get(`${API}/admin/realm-analytics`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setOffers(offersRes.data.offers);
      setRealtimeStats(realtimeRes.data);
      setRealmAnalytics(realmRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.post(`${API}/admin/pricing`, pricingData);
      await axios.post(`${API}/admin/token-pricing`, tokenPackPrices);
      toast.success("Pricing updated successfully!");
      setPricingOpen(false);
      fetchPricing(); // Refresh pricing display
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update pricing");
    } finally {
      setSavingPricing(false);
    }
  };

  const handleCreateOffer = async () => {
    setSavingOffer(true);
    try {
      await axios.post(`${API}/admin/offers`, offerData);
      toast.success("Offer created successfully!");
      setOfferOpen(false);
      setOfferData({
        target_type: "all",
        target_email: "",
        offer_type: "free_months",
        value: 1,
        description: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create offer");
    } finally {
      setSavingOffer(false);
    }
  };

  const handleBanUser = async (email) => {
    if (!window.confirm(`Are you sure you want to ban ${email}?`)) return;
    
    try {
      await axios.patch(`${API}/admin/users/${encodeURIComponent(email)}/ban`);
      toast.success("User banned");
      fetchData();
    } catch (error) {
      toast.error("Failed to ban user");
    }
  };

  const openSendTokens = (user) => {
    setSendTokensUser(user);
    setSendTokensAmount(50);
    setSendTokensReason("");
    setSendTokensOpen(true);
  };

  // -----------------------------------------------------------------
  // Weekend leaderboard controls — manual open/close for QA + emergencies.
  // -----------------------------------------------------------------
  const [lbBusy, setLbBusy] = useState(false);
  const lbAction = async (mode) => {
    if (lbBusy) return;
    const verb = mode === "open" ? "Open" : "Close";
    if (!window.confirm(`${verb} the community weekend leaderboard now?`)) return;
    setLbBusy(true);
    try {
      await axios.post(`${API}/community/admin/leaderboard/${mode === "open" ? "recompute" : "close"}`);
      toast.success(`Leaderboard ${mode === "open" ? "opened" : "closed"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${verb.toLowerCase()} leaderboard.`);
    } finally {
      setLbBusy(false);
    }
  };

  const handleSendTokens = async () => {
    if (!sendTokensUser) return;
    const amount = parseInt(sendTokensAmount, 10);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Please enter a valid token amount");
      return;
    }
    if (amount > 5000) {
      toast.error("Maximum 5000 tokens per send");
      return;
    }

    setSendingTokens(true);
    try {
      // 1) Create a tokens offer targeted at this user
      const createRes = await axios.post(`${API}/admin/offers`, {
        target_type: "specific",
        target_email: sendTokensUser.email,
        offer_type: "tokens",
        value: amount,
        description: sendTokensReason?.trim() || `Admin token grant: ${amount} tokens`
      });
      const offerId = createRes.data?.offer_id;
      if (!offerId) {
        throw new Error("No offer_id returned");
      }

      // 2) Immediately apply the offer to credit the user's balance
      await axios.post(
        `${API}/admin/offers/${offerId}/apply/${encodeURIComponent(sendTokensUser.email)}`
      );

      toast.success(`Sent ${amount} tokens to ${sendTokensUser.email}`);
      setSendTokensOpen(false);
      setSendTokensUser(null);
      fetchData();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || "Failed to send tokens";
      toast.error(msg);
    } finally {
      setSendingTokens(false);
    }
  };

  const handleUnbanUser = async (email) => {
    try {
      await axios.patch(`${API}/admin/users/${encodeURIComponent(email)}/unban`);
      toast.success("User unbanned");
      fetchData();
    } catch (error) {
      toast.error("Failed to unban user");
    }
  };

  const handleUpdateSubscription = async (email, tier) => {
    try {
      const r = await axios.patch(`${API}/admin/users/${encodeURIComponent(email)}/subscription`, { tier });
      const granted = r?.data?.tokens_granted || 0;
      if (granted > 0) {
        toast.success(`Upgraded to ${tier} + ${granted} tokens credited`);
      } else {
        toast.success(`User set to ${tier}`);
      }
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update subscription");
    }
  };

  const filteredUsers = searchEmail 
    ? users.filter(u => u.email.toLowerCase().includes(searchEmail.toLowerCase()))
    : users;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ink-950"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-ink-950 tracking-tight">
            Admin Dashboard
          </h1>
          <p className="text-ink-500 mt-1">Manage users, pricing, and offers</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20">
          <Shield className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-amber-300">Admin Access</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-6 rounded-2xl bg-white border border-ink-200">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-ink-500">Real Users</span>
            <Users className="w-5 h-5 text-coral-500" />
          </div>
          <span className="text-3xl font-bold text-ink-950">{realtimeStats?.total_real_users || stats?.total_users || 0}</span>
          <p className="text-xs text-ink-500 mt-1">Excludes test accounts</p>
        </div>

        <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-900/50 to-emerald-800/30 border border-coral-500/30">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-ink-700">Monthly Revenue</span>
            <DollarSign className="w-5 h-5 text-coral-500" />
          </div>
          <span className="text-3xl font-bold text-coral-500">${realtimeStats?.monthly_recurring_revenue || 0}</span>
          <p className="text-xs text-coral-500/70 mt-1">MRR (real-time)</p>
        </div>

        <div className="p-6 rounded-2xl bg-white border border-ink-200">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-ink-500">Total Content</span>
            <TrendingUp className="w-5 h-5 text-coral-500" />
          </div>
          <span className="text-3xl font-bold text-ink-950">{stats?.total_content || 0}</span>
        </div>

        <div className="p-6 rounded-2xl bg-white border border-ink-200">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-ink-500">Banned Users</span>
            <Ban className="w-5 h-5 text-red-400" />
          </div>
          <span className="text-3xl font-bold text-red-400">{stats?.banned_users || 0}</span>
        </div>
      </div>

      {/* Subscription Breakdown - Real-time */}
      <div className="p-6 rounded-2xl bg-white border border-ink-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-ink-950">Subscription Breakdown</h3>
          <span className="text-xs text-coral-500 px-2 py-1 rounded-full bg-coral-500/10 border border-coral-500/20">
            🔴 Live
          </span>
        </div>
        <div className="grid sm:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-ink-100 text-center">
            <span className="text-2xl font-bold text-ink-700">{realtimeStats?.subscriptions?.free || 0}</span>
            <p className="text-sm text-ink-500 mt-1">Free</p>
          </div>
          <div className="p-4 rounded-xl bg-coral-500/20 text-center">
            <span className="text-2xl font-bold text-violet-300">{realtimeStats?.subscriptions?.premium || 0}</span>
            <p className="text-sm text-coral-500 mt-1">Premium (${pricingData.premium_price})</p>
          </div>
          <div className="p-4 rounded-xl bg-amber-500/20 text-center">
            <span className="text-2xl font-bold text-amber-300">{realtimeStats?.subscriptions?.platinum || 0}</span>
            <p className="text-sm text-amber-400 mt-1">Platinum (${pricingData.platinum_price})</p>
          </div>
          <div className="p-4 rounded-xl bg-purple-500/20 text-center">
            <span className="text-2xl font-bold text-purple-300">{realtimeStats?.subscriptions?.ultimate || 0}</span>
            <p className="text-sm text-purple-400 mt-1">Ultimate (Admin)</p>
          </div>
        </div>
        <p className="text-xs text-ink-500 mt-4 text-center">
          Today's signups: {realtimeStats?.today_signups || 0} • Test accounts excluded
        </p>
      </div>

      {/* Admin Actions */}
      <div className="grid sm:grid-cols-1 gap-4 max-w-md">
        {/* Pricing Dialog */}
        <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
          <DialogTrigger asChild>
            <Button className="h-20 bg-white border border-ink-200 hover:border-coral-500/40 flex flex-col items-center justify-center gap-2" data-testid="manage-pricing-btn">
              <DollarSign className="w-6 h-6 text-coral-500" />
              <span className="text-ink-950">Manage Pricing</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-ink-200">
            <DialogHeader>
              <DialogTitle className="text-ink-950">Update Subscription Pricing</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {[
                { id: "free_price",     label: "Free Plan Price ($)",     test: "free-price-input" },
                { id: "starter_price",  label: "Starter Plan Price ($)",  test: "starter-price-input" },
                { id: "premium_price",  label: "Premium Plan Price ($)",  test: "premium-price-input" },
                { id: "pro_price",      label: "Pro Plan Price ($)",      test: "pro-price-input" },
                { id: "platinum_price", label: "Platinum Plan Price ($)", test: "platinum-price-input" },
              ].map((row) => (
                <div key={row.id} className="space-y-2">
                  <Label className="text-ink-950">{row.label}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricingData[row.id]}
                    onChange={(e) => setPricingData({ ...pricingData, [row.id]: parseFloat(e.target.value || "0") })}
                    className="bg-ink-100 border-ink-200 text-ink-950"
                    data-testid={row.test}
                  />
                </div>
              ))}
              <div className="pt-2 border-t border-ink-200">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500 mb-3">Token pack prices</p>
                {[
                  { id: "pack_50",  label: "50 Tokens Pack ($)",  test: "pack-50-price-input" },
                  { id: "pack_150", label: "150 Tokens Pack ($)", test: "pack-150-price-input" },
                  { id: "pack_500", label: "500 Tokens Pack ($)", test: "pack-500-price-input" },
                ].map((row) => (
                  <div key={row.id} className="space-y-2 mb-3">
                    <Label className="text-ink-950">{row.label}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={tokenPackPrices[row.id]}
                      onChange={(e) => setTokenPackPrices({ ...tokenPackPrices, [row.id]: parseFloat(e.target.value || "0") })}
                      className="bg-ink-100 border-ink-200 text-ink-950"
                      data-testid={row.test}
                    />
                  </div>
                ))}
              </div>
              <Button
                onClick={handleUpdatePricing}
                disabled={savingPricing}
                className="w-full bg-coral-500 hover:bg-coral-600"
                data-testid="save-pricing-btn"
              >
                {savingPricing ? "Saving..." : "Update Pricing"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Offer button moved to the dedicated Admin → Offers page.
            (Kept here as a no-op comment so future agents know it's intentional.) */}
      </div>

      {/* Realm Activity (per-mode analytics, last 7 days) */}
      {realmAnalytics && (
        <div className="p-6 rounded-2xl bg-white border border-ink-200" data-testid="realm-analytics-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-ink-950">Realm Activity</h3>
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
              last {realmAnalytics.window_days || 7}d · 🧠 {realmAnalytics.brain_events_7d || 0} brain events
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { id: "marketing", label: "Marketing", color: "#FF4F27" },
              { id: "designer",  label: "Designing", color: "#8B5CF6" },
              { id: "cgi",       label: "CGI",       color: "#3B82F6" },
              { id: "movie",     label: "Movie",     color: "#F59E0B" },
            ].map((r) => {
              const block = (realmAnalytics.per_realm || {})[r.id] || { renders: 0, community_posts: 0 };
              const isMovie = r.id === "movie";
              const ratio = block.completion_ratio;
              return (
                <div
                  key={r.id}
                  className="rounded-xl border p-3"
                  style={{ borderColor: `${r.color}33`, background: `${r.color}07` }}
                  data-testid={`realm-stat-${r.id}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: r.color }}>
                    {r.label}
                  </div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-2xl font-black font-display text-ink-950">{block.renders}</span>
                    <span className="text-[10px] text-ink-500">{isMovie ? "shipped" : "renders"}</span>
                  </div>
                  <div className="text-[11px] text-ink-500 mt-0.5">
                    {block.community_posts} community posts
                  </div>
                  {isMovie && block.scripts_approved > 0 && (
                    <div
                      className="text-[10px] text-ink-700 mt-1 font-semibold"
                      data-testid="movie-completion-ratio"
                    >
                      {block.films_shipped}/{block.scripts_approved} approved · {ratio !== null ? `${Math.round(ratio * 100)}%` : "—"} ship rate
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Active Offers */}
      {offers.length > 0 && (
        <div className="p-6 rounded-2xl bg-white border border-ink-200">
          <h3 className="text-lg font-semibold text-ink-950 mb-4">Active Offers</h3>
          <div className="space-y-3">
            {offers.slice(0, 5).map((offer) => (
              <div key={offer.id} className="p-4 rounded-xl bg-ink-100 flex items-center justify-between">
                <div>
                  <p className="text-ink-950 font-medium">{offer.description || "Special Offer"}</p>
                  <p className="text-sm text-ink-500">
                    {offer.target_type === "all" ? "All users" : offer.target_email} • 
                    {offer.offer_type === "free_months"
                      ? ` ${offer.value} free month(s)`
                      : offer.offer_type === "tokens"
                      ? ` 🪙 ${offer.value} tokens`
                      : ` ${offer.value}% off`}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${offer.is_active ? 'bg-coral-500/20 text-coral-500' : 'bg-ink-200 text-ink-500'}`}>
                  {offer.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekend Leaderboard Controls (QA / emergency override) */}
      <div className="p-6 rounded-2xl bg-white border border-ink-200" data-testid="leaderboard-controls">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-ink-950">Weekend Leaderboard</h3>
            <p className="text-sm text-ink-500 mt-1">
              Normally auto-opens Saturday 00:00 and closes Sunday 12:00 (Europe/Nicosia).
              Use these buttons for QA or off-window testing — they trigger the same jobs
              the scheduler runs.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => lbAction("open")}
              disabled={lbBusy}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="leaderboard-open-now-btn"
            >
              {lbBusy ? "Working..." : "Open now"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => lbAction("close")}
              disabled={lbBusy}
              data-testid="leaderboard-close-now-btn"
            >
              Close now
            </Button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="p-6 rounded-2xl bg-white border border-ink-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h3 className="text-lg font-semibold text-ink-950">User Management</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
            <Input
              type="text"
              placeholder="Search by email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="pl-10 bg-ink-100 border-ink-200 text-ink-950 w-64"
              data-testid="user-search-input"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-200">
                <th className="text-left py-3 px-4 text-sm text-ink-500 font-medium">User</th>
                <th className="text-left py-3 px-4 text-sm text-ink-500 font-medium">Plan</th>
                <th className="text-left py-3 px-4 text-sm text-ink-500 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-sm text-ink-500 font-medium">Tokens</th>
                <th className="text-right py-3 px-4 text-sm text-ink-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="border-b border-ink-200/50 hover:bg-ink-100/30">
                  <td className="py-3 px-4">
                    <div>
                      <p className="text-ink-950 font-medium">{user.name}</p>
                      <p className="text-sm text-ink-500">{user.email}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs capitalize ${
                      user.subscription_tier === "platinum" ? "bg-amber-500/20 text-amber-300" :
                      user.subscription_tier === "premium" ? "bg-coral-500/20 text-violet-300" :
                      "bg-ink-200 text-ink-700"
                    }`}>
                      {user.subscription_tier}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {user.is_banned ? (
                      <span className="flex items-center gap-1 text-red-400 text-sm">
                        <Ban className="w-4 h-4" /> Banned
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-coral-500 text-sm">
                        <CheckCircle className="w-4 h-4" /> Active
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-ink-700">
                    {user.tokens || 0} tokens
                  </td>
                  <td className="py-3 px-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-ink-500 hover:text-ink-950">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-ink-100 border-ink-200">
                        <DropdownMenuItem
                          onClick={() => openSendTokens(user)}
                          className="text-amber-600 hover:bg-amber-50 hover:text-amber-700 cursor-pointer font-medium"
                          data-testid={`send-tokens-action-${user.email}`}
                        >
                          <Coins className="w-4 h-4 mr-2" />
                          Send Tokens
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleUpdateSubscription(user.email, "free")}
                          className="text-ink-700 hover:bg-ink-200 hover:text-ink-950 cursor-pointer"
                          data-testid={`set-tier-free-${user.email}`}
                        >
                          Set to Free <span className="ml-auto text-[10px] text-ink-400">100 tk</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleUpdateSubscription(user.email, "starter")}
                          className="text-ink-700 hover:bg-ink-200 hover:text-ink-950 cursor-pointer"
                          data-testid={`set-tier-starter-${user.email}`}
                        >
                          Set to Starter <span className="ml-auto text-[10px] text-ink-400">100 tk</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleUpdateSubscription(user.email, "premium")}
                          className="text-ink-700 hover:bg-ink-200 hover:text-ink-950 cursor-pointer"
                          data-testid={`set-tier-premium-${user.email}`}
                        >
                          Set to Premium <span className="ml-auto text-[10px] text-ink-400">200 tk</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleUpdateSubscription(user.email, "pro")}
                          className="text-ink-700 hover:bg-ink-200 hover:text-ink-950 cursor-pointer"
                          data-testid={`set-tier-pro-${user.email}`}
                        >
                          Set to Pro <span className="ml-auto text-[10px] text-ink-400">300 tk</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleUpdateSubscription(user.email, "platinum")}
                          className="text-ink-700 hover:bg-ink-200 hover:text-ink-950 cursor-pointer"
                          data-testid={`set-tier-platinum-${user.email}`}
                        >
                          Set to Platinum <span className="ml-auto text-[10px] text-ink-400">400 tk</span>
                        </DropdownMenuItem>
                        {user.is_banned ? (
                          <DropdownMenuItem
                            onClick={() => handleUnbanUser(user.email)}
                            className="text-coral-500 hover:bg-ink-200 cursor-pointer"
                          >
                            Unban User
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handleBanUser(user.email)}
                            className="text-red-400 hover:bg-ink-200 cursor-pointer"
                          >
                            Ban User
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Send Tokens Dialog (quick-action from user row) */}
      <Dialog open={sendTokensOpen} onOpenChange={setSendTokensOpen}>
        <DialogContent className="bg-white border-ink-200" data-testid="send-tokens-dialog">
          <DialogHeader>
            <DialogTitle className="text-ink-950 flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-500" />
              Send Tokens
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {sendTokensUser && (
              <div className="p-3 rounded-xl bg-ink-100 border border-ink-200">
                <p className="text-sm text-ink-500">Recipient</p>
                <p className="text-ink-950 font-medium">{sendTokensUser.name || "—"}</p>
                <p className="text-sm text-ink-700">{sendTokensUser.email}</p>
                <p className="text-xs text-ink-500 mt-1">
                  Current balance: <span className="text-ink-950 font-medium">{sendTokensUser.tokens || 0} tokens</span>
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-ink-950">Amount (1 – 5000)</Label>
              <Input
                type="number"
                min={1}
                max={5000}
                value={sendTokensAmount}
                onChange={(e) => setSendTokensAmount(e.target.value)}
                className="bg-ink-100 border-ink-200 text-ink-950"
                data-testid="send-tokens-amount-input"
              />
              <div className="flex gap-2 flex-wrap">
                {[10, 25, 50, 100, 250, 500].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setSendTokensAmount(preset)}
                    className="px-3 py-1 text-xs rounded-full bg-ink-100 hover:bg-amber-500/10 border border-ink-200 hover:border-amber-500/40 text-ink-700 transition"
                    data-testid={`send-tokens-preset-${preset}`}
                  >
                    +{preset}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-ink-950">Reason (optional)</Label>
              <Textarea
                value={sendTokensReason}
                onChange={(e) => setSendTokensReason(e.target.value)}
                placeholder="e.g. Refund for failed render, loyalty bonus, etc."
                className="bg-ink-100 border-ink-200 text-ink-950"
                data-testid="send-tokens-reason-input"
              />
            </div>

            <Button
              onClick={handleSendTokens}
              disabled={sendingTokens || !sendTokensUser}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="confirm-send-tokens-btn"
            >
              {sendingTokens
                ? "Sending..."
                : `Send ${parseInt(sendTokensAmount, 10) || 0} tokens`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardPage;
